import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History, Pencil, Plus, Trash2, X } from "lucide-react";
import { Banner, Button, DataTable, Drawer, ErrorState, LoadingState, Modal, SearchInput, TableEmpty, Toggle, useToast, type Column } from "@/shared/rd";
import type { FeatureFlagView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import { FeatureFlagFormDialog, type FlagFormTarget } from "../components/FeatureFlagFormDialog";
import { FeatureFlagAuditPanel } from "../components/FeatureFlagAuditPanel";
import { useFeatureFlagsQuery } from "../services/useFeatureFlagsQuery";
import { useUpdateFeatureFlagMutation } from "../services/useUpdateFeatureFlagMutation";
import { useDeleteFeatureFlagMutation } from "../services/useDeleteFeatureFlagMutation";

type Props = {
  locale: string;
};

/** Deleting is the one row action that asks first: it cannot be undone. */
type PendingDelete = FeatureFlagView | null;

/** A failed row action: what failed, why, and the same action again for "Try again". */
type Failure = { title: string; message: string; retry: () => Promise<unknown> };

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * In-house feature flags, super admins only: a search, then the framed table
 * (flag, an Active switch, targeting, last update, row actions). Create and
 * edit open in a drawer, the audit trail in a second one. Switching a flag
 * happens at once and offers Undo (switching it back is the same request);
 * deleting asks first. A failed action shows an error banner above the table.
 */
export function FeatureFlagsPage({ locale }: Props) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const isSuperAdmin = session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;
  const [form, setForm] = useState<FlagFormTarget>({ mode: "closed" });
  const [historyFlag, setHistoryFlag] = useState<FeatureFlagView | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);
  const [query, setQuery] = useState("");
  const [failure, setFailure] = useState<Failure | null>(null);
  const toast = useToast();
  const update = useUpdateFeatureFlagMutation();
  const remove = useDeleteFeatureFlagMutation();
  const isMutating = update.isPending || remove.isPending;

  useEffect(() => {
    if (!isSessionLoading && !isSuperAdmin) {
      navigate(`/${locale}/admin`, { replace: true });
    }
  }, [isSessionLoading, isSuperAdmin, locale, navigate]);

  const { data: flags, isLoading, error: loadError, refetch, isRefetching } = useFeatureFlagsQuery();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (flags ?? []).filter((flag) => {
      if (!q) return true;
      const name = (locale === "ar" ? flag.nameAr : flag.nameEn) ?? "";
      return flag.key.toLowerCase().includes(q) || name.toLowerCase().includes(q);
    });
  }, [flags, query, locale]);

  if (isSessionLoading || !isSuperAdmin || isLoading) {
    return (
      <LoadingState title={t(keys.shell.loadingTitle, { page: t("featureFlags.loadingPage") })} sub={t(keys.shell.loadingSub)} rows={6} />
    );
  }
  if (loadError || !flags) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: t("featureFlags.loadingPage") })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={isRefetching ? t(keys.shell.retrying) : t(keys.shell.retry)}
        retrying={isRefetching}
      />
    );
  }

  const nameOf = (flag: FeatureFlagView) => (locale === "ar" ? flag.nameAr : flag.nameEn) || flag.nameEn;

  const messageOf = (err: unknown) =>
    err instanceof ApiJsonClientError ? err.message || t(keys.errors.generic) : t(keys.errors.generic);

  /**
   * Runs one row action, then `onOk`. A failure lands on the banner above the
   * table, whose Try again runs the same action and the same follow-up.
   */
  const attempt = async (title: string, run: () => Promise<unknown>, onOk: () => void): Promise<boolean> => {
    try {
      await run();
    } catch (err) {
      setFailure({ title, message: messageOf(err), retry: () => attempt(title, run, onOk) });
      return false;
    }
    setFailure(null);
    onOk();
    return true;
  };

  /** Switch now; Undo sends the opposite switch for the same flag. */
  const switchFlag = (flag: FeatureFlagView, next: boolean) => {
    const name = nameOf(flag);
    const send = (isActive: boolean, onOk: () => void) =>
      attempt(
        t("featureFlags.switchFailedTitle", { name }),
        () => update.mutateAsync({ id: flag.id, isActive }),
        onOk,
      );
    void send(next, () =>
      toast.show({
        tone: "neutral",
        message: t(next ? "featureFlags.toastTurnedOn" : "featureFlags.toastTurnedOff", { name }),
        actionLabel: t("featureFlags.undo"),
        onAction: () =>
          void send(!next, () =>
            toast.show({
              tone: "neutral",
              message: t(!next ? "featureFlags.toastTurnedOn" : "featureFlags.toastTurnedOff", { name }),
            }),
          ),
      }),
    );
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const flag = pendingDelete;
    const name = nameOf(flag);
    await attempt(
      t("featureFlags.deleteFailedTitle", { name }),
      () => remove.mutateAsync(flag.id),
      () => toast.show({ message: t("featureFlags.toastDeletedNamed", { name }) }),
    );
    setPendingDelete(null);
  };

  const columns: Array<Column<FeatureFlagView>> = [
    {
      key: "name",
      label: t("featureFlags.columnFlag"),
      width: 2.4,
      render: (flag) => {
        const description = locale === "ar" ? flag.descriptionAr : flag.descriptionEn;
        return (
          <div className="rd-cell-stack">
            <span dir="auto" style={{ fontWeight: 500 }}>
              {nameOf(flag)}
            </span>
            <span className="rd-cell-sub" dir="ltr" style={{ fontFamily: MONO, textAlign: "start" }}>
              {flag.key}
            </span>
            {description ? (
              <span className="rd-cell-sub" dir="auto">
                {description}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "active",
      label: t(keys.common.status),
      width: 1,
      render: (flag) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Toggle
            size="sm"
            checked={flag.isActive}
            disabled={isMutating}
            label={`${nameOf(flag)} — ${t(keys.featureFlags.formActiveLabel)}`}
            onChange={(next) => switchFlag(flag, next)}
          />
          <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
            {flag.isActive ? t(keys.featureFlags.statusActive) : t(keys.featureFlags.statusInactive)}
          </span>
        </span>
      ),
    },
    {
      key: "target",
      label: t("featureFlags.columnTarget"),
      width: 1.1,
      render: (flag) => (
        <span style={{ color: !flag.enableForAllStores && flag.targetStores.length === 0 ? "var(--rd-text-meta)" : undefined }}>
          {targetingLabel(flag, t)}
        </span>
      ),
    },
    {
      key: "updated",
      label: t("featureFlags.columnUpdated"),
      width: 0.9,
      align: "end",
      numeric: true,
      render: (flag) => <span style={{ color: "var(--rd-text-secondary)" }}>{formatDate(flag.updatedAt, locale)}</span>,
    },
    {
      key: "actions",
      label: "",
      width: "112px",
      align: "end",
      render: (flag) => (
        <span style={{ display: "inline-flex", gap: 2 }}>
          <Button
            variant="ghost"
            iconOnly
            icon={History}
            title={t(keys.featureFlags.showHistory)}
            aria-label={`${t(keys.featureFlags.showHistory)}: ${nameOf(flag)}`}
            onClick={() => setHistoryFlag(flag)}
          />
          <Button
            variant="ghost"
            iconOnly
            icon={Pencil}
            title={t(keys.featureFlags.editAction)}
            aria-label={`${t(keys.featureFlags.editAction)}: ${nameOf(flag)}`}
            disabled={isMutating}
            onClick={() => setForm({ mode: "edit", flag })}
          />
          <Button
            variant="ghost"
            iconOnly
            icon={Trash2}
            title={t(keys.featureFlags.deleteAction)}
            aria-label={`${t(keys.featureFlags.deleteAction)}: ${nameOf(flag)}`}
            disabled={isMutating}
            onClick={() => setPendingDelete(flag)}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeaderActions>
        <Button variant="primary" size="sm" icon={Plus} onClick={() => setForm({ mode: "create" })}>
          {t(keys.featureFlags.addAction)}
        </Button>
      </PageHeaderActions>

      {failure ? (
        <Banner
          tone="error"
          title={failure.title}
          actions={
            <Button size="sm" loading={isMutating} loadingLabel={t(keys.shell.retrying)} onClick={() => void failure.retry()}>
              {t(keys.shell.retry)}
            </Button>
          }
          onDismiss={() => setFailure(null)}
          dismissLabel={t(keys.common.close)}
        >
          {`${failure.message} ${t("featureFlags.nothingChanged")}`}
        </Banner>
      ) : null}

      {flags.length > 0 ? (
        <div className="rd-toolbar">
          <SearchInput
            name="flag-search"
            value={query}
            placeholder={t(keys.featureFlags.searchPlaceholder)}
            aria-label={t(keys.featureFlags.searchPlaceholder)}
            onChange={(event) => setQuery(event.target.value)}
            style={{ width: 280, maxWidth: "100%" }}
          />
          <span className="rd-num" style={{ fontSize: 13, color: "var(--rd-text-meta)" }}>
            {t("featureFlags.countLabel", { count: filtered.length })}
          </span>
        </div>
      ) : null}
      <DataTable
        label={t(keys.featureFlags.title)}
        columns={columns}
        rows={filtered}
        rowKey={(flag) => flag.id}
        isDimmed={(flag) => !flag.isActive}
        minWidth={880}
        empty={
          flags.length === 0 ? (
            <TableEmpty
              illustration="admin-empty-table"
              title={t(keys.featureFlags.emptyTitle)}
              actions={
                <Button variant="primary" size="sm" icon={Plus} onClick={() => setForm({ mode: "create" })}>
                  {t(keys.featureFlags.addAction)}
                </Button>
              }
            >
              {t(keys.featureFlags.emptySubtitle)}
            </TableEmpty>
          ) : (
            <TableEmpty
              illustration="admin-no-results"
              title={t("featureFlags.noResultsFor", { query: query.trim() })}
              actions={
                <Button variant="secondary" size="sm" icon={X} onClick={() => setQuery("")}>
                  {t(keys.common.clearFilters)}
                </Button>
              }
            >
              {t("featureFlags.noResultsSub")}
            </TableEmpty>
          )
        }
      />

      <FeatureFlagFormDialog
        target={form}
        onClose={() => setForm({ mode: "closed" })}
        onSaved={(message) => {
          setForm({ mode: "closed" });
          toast.show({ message });
        }}
      />

      <Drawer
        open={historyFlag !== null}
        onClose={() => setHistoryFlag(null)}
        closeLabel={t(keys.common.close)}
        width={500}
        badge="clock"
        crumb={`${t(keys.featureFlags.title)} / ${historyFlag?.key ?? ""}`}
        title={historyFlag ? nameOf(historyFlag) : undefined}
        sub={historyFlag ? t("featureFlags.historySub") : undefined}
      >
        {historyFlag ? <FeatureFlagAuditPanel flagId={historyFlag.id} /> : null}
      </Drawer>

      <Modal
        open={pendingDelete !== null}
        onClose={remove.isPending ? undefined : () => setPendingDelete(null)}
        width={440}
        title={pendingDelete ? t("featureFlags.deleteNamedTitle", { name: nameOf(pendingDelete) }) : undefined}
        actions={
          <>
            <Button variant="ghost" muted disabled={remove.isPending} onClick={() => setPendingDelete(null)}>
              {t(keys.common.cancel)}
            </Button>
            <Button variant="danger" loading={remove.isPending} loadingLabel={t("featureFlags.deleting")} onClick={() => void confirmDelete()}>
              {t(keys.featureFlags.confirmDeleteAction)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.featureFlags.confirmDeleteBody)}
        </p>
      </Modal>
    </>
  );
}

function targetingLabel(flag: FeatureFlagView, t: ReturnType<typeof useTranslations>): string {
  if (flag.enableForAllStores) return t(keys.featureFlags.targetAllStores);
  if (flag.targetStores.length === 0) return t(keys.featureFlags.targetNoStores);
  return t(keys.featureFlags.targetStoresCount, { count: flag.targetStores.length });
}

function formatDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale, { dateStyle: "medium" });
}
